import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { upsertUserToDashboard } from "../lib/authService";

const AuthContext = createContext(null);
const DASHBOARD_SESSION_KEY = 'ayrine_dashboard_session';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function initAuth() {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        upsertUserToDashboard(session.user);
      } else {
        setUser(null);
        // Optional: Enable "Anonymous Sign-ins" in Supabase Dashboard (Auth > Providers)
        // to allow guest cart/wishlist. Otherwise guests use the site without persisted cart.
      }
      setLoading(false);
    }
    initAuth();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user && !session.user.is_anonymous) upsertUserToDashboard(session.user);
      if (!session) sessionStorage.removeItem(DASHBOARD_SESSION_KEY);
    });

    return () => subscription?.unsubscribe();
  }, []);

  const value = {
    user,
    loading,
    signIn: (email, password) => supabase.auth.signInWithPassword({ email, password }),
    signUp: (email, password) => supabase.auth.signUp({ email, password }),
    signInWithOtp: (email, options = {}) => supabase.auth.signInWithOtp({ email, options }),
    verifyOtp: (email, token) => supabase.auth.verifyOtp({ email, token, type: 'email' }),
    updateUser: (attrs) => supabase.auth.updateUser(attrs),
    signInWithGoogle: () =>
      supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: `${window.location.origin}/` },
      }),
    signOut: () => {
      sessionStorage.removeItem(DASHBOARD_SESSION_KEY);
      return supabase.auth.signOut();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
